import React, { useState, useEffect } from "react";
import { FaSave } from 'react-icons/fa';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import '../Patients/CreatePatient.css'; // Assuming you're using the same CSS
import AccessControl from '../AccessControl';

const CreateAppointment = ({ onAddAppointment }) => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [customReason, setCustomReason] = useState('');
    const [patientName, setPatientName] = useState('');
    const [email, setEmail] = useState('');
    const [reason, setReason] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [dentist, setDentist] = useState('');
    const [dentistList, setDentistList] = useState([]);
    const [error, setError] = useState(null);
    const [emptyFields, setEmptyFields] = useState([]);
    const [existingAppointments, setExistingAppointments] = useState([]); // Store existing appointments
    const [availableTimes, setAvailableTimes] = useState([]); // Store available times for the selected date
    const [treatments, setTreatments] = useState([]); // Store treatment reasons
    const [showCustomReason, setShowCustomReason] = useState(false); // Toggle custom reason input
    const [blockedDates, setBlockedDates] = useState([]); // Store blocked dates
    const [blockedDatesAndTime, setBlockedDatesAndTime] = useState([]); // Store blocked dates and times
    const [validDates, setValidDates] = useState([]); // Store valid dates for the dropdown
    const [holidays, setHolidays] = useState([]); // Store holidays
    const [patientLimits, setPatientLimits] = useState([]); // Store patient limits
    const [patientID, setPatientID] = useState('');
    const [patients, setPatients] = useState([]);
    const [dentistTreatments, setDentistTreatments] = useState([]);
    const [dentistId, setDentistId] = useState('');

    useEffect(() => {
        const fetchDentistData = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/dentists');
                const allDentists = await response.json();

                // Filter for exact match (case insensitive)
                const matchedDentists = allDentists.filter(d =>
                    d.lastName.toLowerCase() === dentist.toLowerCase()
                );

                if (matchedDentists.length === 1) {
                    const dentistData = matchedDentists[0];
                    setDentistId(dentistData._id);

                    // Fetch treatments
                    const treatmentsRes = await fetch(
                        `https://api.hgcdental.clinic/api/dentists/${dentistData._id}/treatments`
                    );
                    const treatments = await treatmentsRes.json();
                    setDentistTreatments(treatments);
                } else {
                    console.error('Dentist not found or ambiguous match');
                    setDentistTreatments([]);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        };

        if (dentist) {
            fetchDentistData();
        }
    }, [dentist]);

    useEffect(() => {
        const fetchPatients = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/patients');
                if (!response.ok) throw new Error('Failed to fetch patients');
                const patientsData = await response.json();
                setPatients(patientsData);
            } catch (error) {
                console.error('Error fetching patients:', error);
            }
        };

        fetchPatients();
    }, []);

    // Fetch dentist ID and treatments when dentist is selected
    useEffect(() => {
        const fetchDentistInfoAndTreatments = async () => {
            if (!dentist) return;
            try {
                // 1. Find dentist by lastName to get ID
                const dentistRes = await fetch(`https://api.hgcdental.clinic/api/dentists?lastName=${dentist}`);
                if (!dentistRes.ok) throw new Error('Failed to fetch dentist info');
                const dentists = await dentistRes.json();
                if (dentists.length === 0) {
                    console.error('Dentist not found');
                    return;
                }
                const dentistData = dentists[0];
                setDentistId(dentistData._id); // Store dentist ID
                // 2. Fetch dentist's specific treatments
                const treatmentsRes = await fetch(`https://api.hgcdental.clinic/api/dentists/${dentistData._id}/treatments`);
                if (!treatmentsRes.ok) throw new Error('Failed to fetch dentist treatments');
                const treatmentsData = await treatmentsRes.json();
                setDentistTreatments(treatmentsData);
                setReason(''); // Reset reason when dentist changes
                setShowCustomReason(false); // Reset custom reason
            } catch (error) {
                console.error('Error fetching dentist info:', error);
            }
        };
        fetchDentistInfoAndTreatments();
    }, [dentist]);


    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const sendDentistNotification = async (dentistName, patientName, appointmentDate, appointmentTime) => {
        try {
            // 1. Find the dentist user
            const dentistResponse = await fetch(`https://api.hgcdental.clinic/api/dentists?lastName=${dentistName}`);
            if (!dentistResponse.ok) throw new Error('Failed to fetch dentist info');
            const dentists = await dentistResponse.json();

            if (dentists.length === 0) {
                console.error('Dentist not found');
                return;
            }

            const dentist = dentists[0];

            // 2. Create the notification message
            const message = `New appointment with ${patientName} on ${formatDate(appointmentDate)} at ${appointmentTime}`;

            // 3. Send the notification
            const notificationResponse = await fetch('https://api.hgcdental.clinic/api/notifications', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: "appointment",
                    message: message,
                    receiver: dentist._id, // Dentist's user ID
                    date: new Date().toISOString(),
                    appointmentDate: appointmentDate,
                    appointmentTime: appointmentTime
                })
            });

            if (!notificationResponse.ok) {
                throw new Error('Failed to send notification');
            }
        } catch (error) {
            console.error('Error sending notification:', error);
            // You might want to handle this error differently
        }
    };

    // Fetch user, dentists, and treatments on component mount
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/currentExternalUser', {
                    credentials: 'include',
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch user');
                }
                const user = await response.json();
                setPatientName(`${user.firstName} ${user.lastName}`);
                setEmail(user.email);
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };

        const fetchDentists = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/dentists');
                if (!response.ok) {
                    throw new Error('Failed to fetch dentists');
                }
                const dentists = await response.json();
                // Filter out archived dentists and store full objects
                const activeDentists = dentists.filter(dentist => !dentist.isArchived);
                setDentistList(activeDentists);
            } catch (error) {
                console.error('Error fetching dentists:', error);
            }
        };
        
        const fetchTreatments = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/treatments');
                if (!response.ok) {
                    throw new Error('Failed to fetch treatments');
                }
                const treatments = await response.json();
                setTreatments(treatments.map(treatment => treatment.Treatmentname)); // Extract treatment names
            } catch (error) {
                console.error('Error fetching treatments:', error);
            }
        };

        fetchUser();
        fetchDentists();
        fetchTreatments();
    }, []);

    useEffect(() => {
        const fetchBlockedDates = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/blockeddates');
                if (!response.ok) throw new Error('Failed to fetch blocked dates');

                const blockedDatesData = await response.json();

                // Only block dates that match the selected dentist
                const filteredBlockedDates = blockedDatesData
                    .filter(item => item.dentist === dentist) // Only include if dentist matches
                    .map(item => new Date(item.date).toISOString().split('T')[0]);

                setBlockedDates(filteredBlockedDates);
            } catch (error) {
                console.error('Error fetching blocked dates:', error);
            }
        };

        // Fetch again when dentist changes
        if (dentist) {
            fetchBlockedDates();
        }
    }, [dentist]); // ✅ Re-run whenever selected dentist changes

    // Fetch blocked dates, blocked dates and times, holidays, and patient limits
    useEffect(() => {
        const fetchBlockedDatesAndTime = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/blockedDatesAndTime');
                if (!response.ok) throw new Error('Failed to fetch blocked dates and time');
                const data = await response.json();
                setBlockedDatesAndTime(data);
            } catch (error) {
                console.error('Error fetching blocked dates:', error);
            }
        };

        const fetchHolidays = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/holidays');
                if (!response.ok) throw new Error('Failed to fetch holidays');
                const holidaysData = await response.json();
                const holidays = holidaysData.map(item => new Date(item.date).toISOString().split('T')[0]);
                setHolidays(holidays);
            } catch (error) {
                console.error('Error fetching holidays:', error);
            }
        };

        const fetchPatientLimits = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/patientLimits');
                if (!response.ok) throw new Error('Failed to fetch patient limits');
                const patientLimitsData = await response.json();
                setPatientLimits(patientLimitsData);
            } catch (error) {
                console.error('Error fetching patient limits:', error);
            }
        };

        fetchBlockedDatesAndTime();
        fetchHolidays();
        fetchPatientLimits();
    }, []);

    // Generate valid dates based on blocked dates, holidays, and patient limits
    useEffect(() => {
        const generateValidDates = () => {
            const today = new Date();
            const oneMonthFromToday = new Date();
            oneMonthFromToday.setMonth(today.getMonth() + 1);

            const validDates = [];

            for (let d = new Date(today); d <= oneMonthFromToday; d.setDate(d.getDate() + 1)) {
                const dateStr = d.toISOString().split('T')[0];

                const isBlocked = blockedDates.includes(dateStr);
                const isHoliday = holidays.includes(dateStr);

                const patientLimit = patientLimits.find(limit =>
                    limit.date.split('T')[0] === dateStr &&
                    (!limit.dentist || limit.dentist === dentist)
                );

                // Count existing appointments on this date for this dentist
                const appointmentCount = existingAppointments.filter(appointment =>
                    new Date(appointment.date).toISOString().split('T')[0] === dateStr &&
                    appointment.dentist === dentist
                ).length;

                const isLimitReached = patientLimit && appointmentCount >= patientLimit.limit;

                if (!isBlocked && !isHoliday && !isLimitReached) {
                    validDates.push(dateStr);
                }
            }

            setValidDates(validDates);
        };

        if (dentist) {
            generateValidDates();
        }
    }, [blockedDates, holidays, patientLimits, dentist, existingAppointments]);

    // Fetch existing appointments for the selected dentist
    useEffect(() => {
        const fetchAppointmentsForDentist = async () => {
            if (!dentist) return;

            try {
                const response = await fetch(`https://api.hgcdental.clinic/api/appointments?dentist=${dentist}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch appointments');
                }
                const appointments = await response.json();
                setExistingAppointments(appointments); // Store existing appointments
            } catch (error) {
                console.error('Error fetching appointments:', error);
            }
        };

        fetchAppointmentsForDentist();
    }, [dentist]);

    // Generate available times based on selected date, dentist, and existing appointments
    useEffect(() => {
        const generateAvailableTimes = () => {
            if (!dentist) return;

            const today = new Date();
            const selectedDate = new Date(date);
            const isToday = selectedDate.toDateString() === today.toDateString();

            const startHour = isToday ? today.getHours() : 8; // Start from current hour if today, else 9 AM
            const startMinute = isToday ? today.getMinutes() : 0;

            const times = [];
            for (let hour = startHour; hour <= 17; hour++) { // Clinic hours: 9 AM to 5 PM
                for (let minute = 0; minute < 60; minute += 30) {
                    if (isToday && hour === startHour && minute < startMinute) {
                        continue; // Skip times that have already passed today
                    }
                    const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                    times.push(time);
                }
            }

            // Filter out times that are already booked
            const bookedTimes = existingAppointments
                .filter((appointment) => {
                    const appointmentDate = new Date(appointment.date).toDateString();
                    const selectedDateStr = selectedDate.toDateString();
                    return appointmentDate === selectedDateStr;
                })
                .map((appointment) => appointment.startTime);

            // Filter out blocked times for the selected dentist and date
            const blockedTimes = blockedDatesAndTime
                .filter((blockedDate) => {
                    try {
                        // Parse the blocked date and time
                        const blockedDateObj = new Date(blockedDate.date);
                        const blockedDateStr = blockedDateObj.toISOString().split('T')[0]; // Get the date part (YYYY-MM-DD)
                        const selectedDateStr = selectedDate.toISOString().split('T')[0]; // Get the selected date part

                        // Check if the blocked date matches the selected date
                        if (blockedDateStr !== selectedDateStr) return false;

                        // If the blocked time has a dentist, check if it matches the selected dentist
                        if (blockedDate.dentist) {
                            return blockedDate.dentist === dentist;
                        }

                        // If no dentist is specified, block the time globally
                        return true;
                    } catch (error) {
                        console.error('Invalid date in blockedDatesAndTime:', blockedDate.date, error);
                        return false; // Skip invalid dates
                    }
                })
                .map((blockedDate) => {
                    try {
                        const time = new Date(blockedDate.date).toISOString().split('T')[1].slice(0, 5); // HH:MM
                        return time;
                    } catch (error) {
                        console.error('Invalid time in blockedDatesAndTime:', blockedDate.date, error);
                        return null;
                    }
                })


            // Combine booked and blocked times
            const unavailableTimes = [...bookedTimes, ...blockedTimes];

            // Filter out unavailable times
            const availableTimes = times.filter((time) => !unavailableTimes.includes(time));
            setAvailableTimes(availableTimes);
        };

        generateAvailableTimes();
    }, [date, dentist, existingAppointments, blockedDatesAndTime]);

    // Filter out blocked dates from validDates
    const filteredValidDates = validDates.filter(date => !blockedDates.includes(date));

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Existing validation code...
        if (startTime >= endTime) {
            setError('Start time cannot be greater than or equal to end time.');
            setEmptyFields(['startTime', 'endTime']);
            return;
        }

        if (!dentist || !reason || !date || !startTime || !patientID) {
            setError('Please fill in all required fields.');
            setEmptyFields(['dentist', 'reason', 'date', 'startTime', 'patientID']);
            return;
        }

        const appointment = {
            patientID,
            patientName,
            email,
            reason: reason === "Other" ? customReason : reason,
            date,
            startTime,
            endTime: calculateEndTime(startTime),
            dentist,
            dentistId
        };

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/appointments', {
                method: 'POST',
                body: JSON.stringify(appointment),
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (!response.ok) {
                const json = await response.json();
                setError(json.error);
                setEmptyFields(json.emptyFields || []);
                return;
            }

            if (response.ok) {
                // Send notification to dentist
                await sendDentistNotification(
                    dentist,
                    patientName,
                    date,
                    startTime
                );

                window.alert('Appointment created successfully!');
                window.location.href = '/appointments';
            }

            const newAppointment = await response.json();
            onAddAppointment(newAppointment);
        } catch (error) {
            console.error('Error creating appointment:', error);
            setError('Failed to create appointment. Please try again.');
        }
    };

    // Calculate end time based on start time
    const calculateEndTime = (startTime) => {
        const [hour, minute] = startTime.split(':').map(Number);
        const endHour = hour + 2; // Add 2 hours to the start time
        return `${String(endHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    };

    // Handle date change
    const handleDateChange = (e) => {
        setDate(e.target.value);
    };

    // Handle start time change
    const handleStartTimeChange = (e) => {
        const selectedTime = e.target.value;
        setStartTime(selectedTime);
        setEndTime(calculateEndTime(selectedTime)); // Automatically set end time
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    // Handle reason change
    const handleReasonChange = (e) => {
        const selectedReason = e.target.value;
        setReason(selectedReason);
        setShowCustomReason(selectedReason === "Other"); // Show custom reason input if "Other" is selected
    };

    return (
        <AccessControl>
            <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Create Appointment" toggleSidebar={toggleSidebar} />
                <div className="create-patient-content">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="create-patient">
                        <div>
                            <button className="create-patient-button" onClick={handleSubmit}>
                                <FaSave /> Save
                            </button>
                        </div>
                        <div className="create-patient-form">
                            <h2>Create New Appointment</h2>

                            <div className="input-group">
                                <label htmlFor="patientID" className="label">Select Patient</label>
                                <select
                                    id="patientID"
                                    value={patientID}
                                    onChange={(e) => {
                                        const selectedPatient = patients.find(p => p.patientID === e.target.value);
                                        setPatientID(e.target.value);
                                        if (selectedPatient) {
                                            setPatientName(selectedPatient.name);
                                            setEmail(selectedPatient.email);
                                        }
                                    }}
                                    className={`input-field ${emptyFields.includes('patientID') ? 'error' : ''}`}
                                >
                                    <option value="">-- Select Patient --</option>
                                    {patients.map(patient => (
                                        <option key={patient._id} value={patient.patientID}>
                                            {patient.patientID} - {patient.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Dentist Field */}
                            {/* Dentist Field */}
                            <div className="input-group">
                                <label className="label">Dentist*</label>
                                <select
                                    value={dentist}
                                    onChange={(e) => setDentist(e.target.value)}
                                    className={`input-field ${emptyFields.includes('dentist') ? 'error' : ''}`}
                                    required
                                >
                                    <option value="">Select Dentist</option>
                                    {dentistList.map((dentist, index) => (
                                        <option key={index} value={dentist.lastName}>
                                            {dentist.firstName} {dentist.lastName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-group">
                                <label htmlFor="patientName" className="label">Patient Name</label>
                                <input
                                    type="text"
                                    id="patientName"
                                    value={patientName}
                                    disabled
                                    className="input-field"
                                />
                            </div>

                            <div className="input-group">
                                <label htmlFor="email" className="label">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    disabled
                                    className="input-field"
                                />
                            </div>

                            {/* Reason Field */}
                            <div className="input-group">
                                <label className="label">Expected Treatment*</label>
                                <select
                                    value={reason}
                                    onChange={handleReasonChange}
                                    className={`input-field ${emptyFields.includes('reason') ? 'error' : ''}`}
                                    required
                                    disabled={!dentist}
                                >
                                    <option value="">Select Reason</option>
                                    {dentistTreatments.map((treatment, index) => (
                                        <option key={index} value={treatment.Treatmentname}>
                                            {treatment.Treatmentname}
                                        </option>
                                    ))}
                                    <option value="Other">Other</option>
                                </select>
                                {showCustomReason && (
                                    <input
                                        type="text"
                                        value={customReason}
                                        onChange={(e) => setCustomReason(e.target.value)}
                                        placeholder="Type your reason"
                                        className={`input-field ${emptyFields.includes('reason') ? 'error' : ''}`}
                                        required
                                        disabled={!dentist}
                                    />
                                )}
                            </div>

                            {/* Appointment Date Field */}
                            <div className="input-group">
                                <label className="label">Appointment Date*</label>
                                <select
                                    value={date}
                                    onChange={handleDateChange}
                                    className={`input-field ${emptyFields.includes('date') ? 'error' : ''}`}
                                    required
                                    disabled={!dentist}
                                >
                                    <option value="">Select Date</option>
                                    {validDates.map((date, index) => (
                                        <option key={index} value={date}>
                                            {formatDate(date)} {/* Format the date here */}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* Start Time Field */}
                            <div className="input-group">
                                <label className="label">Time*</label>
                                <select
                                    value={startTime}
                                    onChange={handleStartTimeChange}
                                    className={`input-field ${emptyFields.includes('startTime') ? 'error' : ''}`}
                                    required
                                    disabled={!dentist}
                                >
                                    <option value="">Select Time</option>
                                    {availableTimes.map((time, index) => (
                                        <option key={index} value={time}>{time}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default CreateAppointment;